import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { timesheetService } from '@services/timesheetService'
import { 
  ArrowLeft,
  User,
  Clock,
  DollarSign,
  Star,
  AlertTriangle,
  Briefcase,
  BarChart3,
  PieChart,
  Calendar
} from 'lucide-react'
import toast from 'react-hot-toast'
import { Timesheet } from '@/types'

interface TimesheetDetail extends Timesheet {
  totalAmount: number
  workingDays: number
  projectCount: number
  dailyHours: Record<string, number>
  projectHours: Record<string, { name: string, hours: number, color: string }>
  entriesByDate: Record<string, any[]>
}

const TimesheetDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<TimesheetDetail | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (id) {
      loadDetail()
    }
  }, [id])

  const loadDetail = async () => {
    if (!id) return
    
    try {
      setLoading(true)
      const response = await timesheetService.getTimesheetDetail(id)
      setDetail(response)
    } catch (error) {
      console.error('Load detail error:', error)
      toast.error('載入申報詳情失敗')
      navigate('/timesheets')
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('zh-TW', {
      style: 'currency',
      currency: 'TWD',
      minimumFractionDigits: 0
    }).format(amount)
  }

  const formatDateRange = (start: string, end: string) => {
    const startDate = new Date(start).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })
    const endDate = new Date(end).toLocaleDateString('zh-TW', { month: '2-digit', day: '2-digit' })
    return `${startDate} - ${endDate}`
  }

  const getHoursGradientClass = (hours: number) => {
    if (hours >= 10) return 'bg-blue-600 text-white'
    if (hours >= 8) return 'bg-blue-500 text-white'
    if (hours >= 6) return 'bg-blue-400 text-white'
    if (hours >= 4) return 'bg-blue-300 text-blue-900'
    if (hours >= 2) return 'bg-blue-200 text-blue-800'
    return 'bg-blue-100 text-blue-700'
  }

  const getStatusBadge = (status: string) => {
    const statusConfig = {
      SUBMITTED: { label: '🔄 審核中', className: 'bg-yellow-100 text-yellow-800' },
      APPROVED: { label: '✅ 已批准', className: 'bg-green-100 text-green-800' },
      REJECTED: { label: '❌ 已駁回', className: 'bg-red-100 text-red-800' },
      DRAFT: { label: '📝 草稿', className: 'bg-gray-100 text-gray-800' }
    }
    
    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.DRAFT
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.className}`}>
        {config.label}
      </span>
    )
  }

  const getQualityLevel = (score: number) => {
    if (score >= 95) return { text: '傑出', color: 'text-purple-600' }
    if (score >= 90) return { text: '優秀', color: 'text-green-600' }
    if (score >= 85) return { text: '良好', color: 'text-blue-600' }
    if (score >= 80) return { text: '符合預期', color: 'text-yellow-600' }
    return { text: '需要改善', color: 'text-red-600' }
  }

  const renderStars = (score: number) => {
    const starCount = Math.floor(score / 20)
    return [...Array(5)].map((_, i) => (
      <Star
        key={i}
        className={`w-4 h-4 ${
          i < starCount ? 'text-yellow-400 fill-current' : 'text-secondary-300'
        }`}
      />
    ))
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  if (!detail) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-secondary-800 mb-2">找不到申報記錄</h2>
          <p className="text-secondary-600">請檢查申報ID是否正確</p>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* 頁面標題 */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button
            onClick={() => navigate('/timesheets')}
            className="btn-ghost flex items-center gap-2"
          >
            <ArrowLeft className="w-5 h-5" />
            返回列表
          </button>
          <div>
            <h1 className="text-2xl font-bold text-secondary-800">工時申報詳情</h1>
            <p className="text-secondary-600 mt-1">
              {formatDateRange(detail.weekStart, detail.weekEnd)}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {getStatusBadge(detail.status)}
        </div>
      </div>

      {/* 申報概要 - 響應式優化 */}
      <div className="bg-white rounded-xl shadow-soft p-4 md:p-6 border border-primary-200">
        <h2 className="text-lg font-semibold text-secondary-800 mb-4 flex items-center gap-2">
          <User className="w-5 h-5 text-primary-600" />
          申報概要
        </h2>
        
        {/* 統計數據 - 手機版3欄並排 */}
        <div className="grid grid-cols-3 gap-3 md:gap-6">
          {/* 總工時 */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 md:gap-2 mb-1 md:mb-2">
              <Clock className="w-4 h-4 md:w-5 md:h-5 text-blue-500" />
              <span className="text-xs md:text-sm text-secondary-600">總工時</span>
            </div>
            <p className="text-lg md:text-2xl font-bold text-secondary-800">{detail.totalHours.toFixed(2)}h</p>
            <p className="text-xs md:text-sm text-secondary-600">{detail.workingDays}個工作日</p>
          </div>

          {/* 品質分數 */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 md:gap-2 mb-1 md:mb-2">
              <Star className="w-4 h-4 md:w-5 md:h-5 text-yellow-500" />
              <span className="text-xs md:text-sm text-secondary-600">品質分數</span>
            </div>
            {detail.qualityScore ? (
              <>
                <p className="text-lg md:text-2xl font-bold text-secondary-800">{detail.qualityScore}分</p>
                <p className={`text-xs md:text-sm ${getQualityLevel(detail.qualityScore).color}`}>
                  ({getQualityLevel(detail.qualityScore).text})
                </p>
                <div className="flex justify-center mt-1">
                  {renderStars(detail.qualityScore)}
                </div>
              </>
            ) : (
              <p className="text-sm md:text-lg text-secondary-500">尚未評分</p>
            )}
          </div>

          {/* 總金額 */}
          <div className="text-center">
            <div className="flex items-center justify-center gap-1 md:gap-2 mb-1 md:mb-2">
              <DollarSign className="w-4 h-4 md:w-5 md:h-5 text-green-500" />
              <span className="text-xs md:text-sm text-secondary-600">總金額</span>
            </div>
            <p className="text-lg md:text-2xl font-bold text-secondary-800">{formatCurrency(detail.totalAmount)}</p>
          </div>
        </div>
      </div>

      {/* 工時分析圖表 - 響應式設計 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* 每日工時分布 */}
        <div className="bg-white rounded-xl shadow-soft p-4 md:p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-secondary-800 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-500" />
            每日工時分布
          </h3>
          
          {/* 桌面版圖表（保持原樣） */}
          <div className="hidden md:block space-y-3">
            {Object.entries(detail.dailyHours).map(([date, hours]) => (
              <div key={date} className="flex items-center justify-between">
                <span className="text-sm text-secondary-600">
                  {new Date(date).toLocaleDateString('zh-TW', { weekday: 'short', month: '2-digit', day: '2-digit' })}
                </span>
                <div className="flex items-center gap-2">
                  <div className="w-32 bg-secondary-200 rounded-full h-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((hours / 12) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-secondary-800 w-12 text-right">
                    {hours.toFixed(2)}h
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {/* 手機版進度條 */}
          <div className="md:hidden space-y-3">
            {Object.entries(detail.dailyHours).map(([date, hours]) => (
              <div key={date} className="flex items-center justify-between">
                <span className="text-sm text-secondary-600 w-12">
                  {new Date(date).toLocaleDateString('zh-TW', { weekday: 'short' })}
                </span>
                <div className="flex items-center gap-2 flex-1">
                  <div className="flex-1 bg-secondary-200 rounded-full h-2 mx-2">
                    <div 
                      className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${Math.min((hours / 12) * 100, 100)}%` }}
                    />
                  </div>
                  <span className="text-sm font-medium text-secondary-800 w-12 text-right">
                    {hours.toFixed(1)}h
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* 專案時間分配 */}
        <div className="bg-white rounded-xl shadow-soft p-4 md:p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-secondary-800 mb-4 flex items-center gap-2">
            <PieChart className="w-5 h-5 text-green-500" />
            專案時間分配
          </h3>
          
          {/* 桌面版圓餅圖樣式（保持原樣） */}
          <div className="hidden md:block space-y-3">
            {Object.entries(detail.projectHours).map(([projectId, project]) => (
              <div key={projectId} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: project.color }}
                  />
                  <span className="text-sm text-secondary-700">{project.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-secondary-600">
                    {((project.hours / detail.totalHours) * 100).toFixed(1)}%
                  </span>
                  <span className="text-sm font-medium text-secondary-800 w-12 text-right">
                    {project.hours.toFixed(2)}h
                  </span>
                </div>
              </div>
            ))}
          </div>
          
          {/* 手機版列表 */}
          <div className="md:hidden space-y-3">
            {Object.entries(detail.projectHours).map(([projectId, project]) => (
              <div key={projectId} className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div 
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: project.color }}
                  />
                  <span className="text-sm text-secondary-700">{project.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-sm text-secondary-600">
                    {((project.hours / detail.totalHours) * 100).toFixed(1)}%
                  </span>
                  <span className="text-sm font-medium text-secondary-800 w-12 text-right">
                    {project.hours.toFixed(1)}h
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* 詳細工時記錄 - 響應式設計 */}
      <div className="bg-white rounded-xl shadow-soft border border-primary-200">
        <div className="px-4 md:px-6 py-3 md:py-4 border-b border-secondary-200">
          <h3 className="text-lg font-semibold text-secondary-800">詳細工時記錄</h3>
        </div>
        
        {/* 桌面版表格 */}
        <div className="hidden md:block overflow-x-auto">
          <table className="w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">日期</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">專案</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">時間範圍</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">工時</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">金額</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-secondary-500 uppercase tracking-wider">工作描述</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-secondary-200">
              {Object.entries(detail.entriesByDate).map(([date, entries]) => (
                <React.Fragment key={date}>
                  {entries.map((entry: any, index: number) => (
                    <tr key={entry.id} className="hover:bg-secondary-50">
                      {index === 0 && (
                        <td 
                          className="px-6 py-4 whitespace-nowrap text-sm font-medium text-secondary-900 border-r border-secondary-200"
                          rowSpan={entries.length}
                        >
                          {new Date(date).toLocaleDateString('zh-TW', { 
                            month: '2-digit', 
                            day: '2-digit',
                            weekday: 'short'
                          })}
                        </td>
                      )}
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-2">
                          <div 
                            className="w-3 h-3 rounded-full"
                            style={{ backgroundColor: entry.project.color }}
                          />
                          <span className="text-sm text-secondary-900">{entry.project.name}</span>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {new Date(entry.startTime).toLocaleTimeString('zh-TW', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })} - {entry.endTime ? new Date(entry.endTime).toLocaleTimeString('zh-TW', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        }) : '進行中'}
                      </td>
                      <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium rounded ${getHoursGradientClass(entry.hours)}`}>
                        {entry.hours}h
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-secondary-900">
                        {formatCurrency(entry.amount)}
                      </td>
                      <td className="px-6 py-4 text-sm text-secondary-700 max-w-xs truncate">
                        {entry.description || '-'}
                      </td>
                    </tr>
                  ))}
                  {/* 日總計行 */}
                  <tr className="bg-secondary-100 font-medium">
                    <td className="px-6 py-2 text-sm text-secondary-800" colSpan={3}>
                      日總計
                    </td>
                    <td className="px-6 py-2 text-sm font-bold text-secondary-900">
                      {detail.dailyHours[date]?.toFixed(2)}h
                    </td>
                    <td className="px-6 py-2 text-sm font-bold text-secondary-900">
                      {formatCurrency(entries.reduce((sum: number, entry: any) => sum + entry.amount, 0))}
                    </td>
                    <td className="px-6 py-2"></td>
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>

        {/* 手機版卡片 */}
        <div className="md:hidden p-4 space-y-4">
          {Object.entries(detail.entriesByDate).map(([date, entries]) => (
            <div key={date} className="space-y-3">
              {/* 日期標題 - 合併總計顯示 */}
              <div className="flex items-center justify-between py-2 bg-secondary-50 rounded-lg px-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-primary-600" />
                  <span className="font-medium text-secondary-800 text-sm">
                    {new Date(date).toLocaleDateString('zh-TW', { 
                      month: '2-digit', 
                      day: '2-digit',
                      weekday: 'short'
                    })}
                  </span>
                </div>
                <div className="flex items-center gap-2 text-xs font-medium text-primary-700">
                  <span>{detail.dailyHours[date]?.toFixed(1)}h</span>
                  <span>•</span>
                  <span>{formatCurrency(entries.reduce((sum: number, entry: any) => sum + entry.amount, 0))}</span>
                </div>
              </div>

              {/* 該日的工時記錄 */}
              <div className="space-y-2 pl-0">
                {entries.map((entry: any) => (
                  <div key={entry.id} className="bg-secondary-50 rounded-lg p-3 border border-secondary-200">
                    {/* 專案和時間行 */}
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: entry.project.color }}
                        />
                        <span className="font-medium text-secondary-800 text-sm">
                          {entry.project.name}
                        </span>
                      </div>
                      <span className="text-xs text-secondary-600">
                        {new Date(entry.startTime).toLocaleTimeString('zh-TW', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })} - {entry.endTime ? new Date(entry.endTime).toLocaleTimeString('zh-TW', { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        }) : '進行中'}
                      </span>
                    </div>

                    {/* 工時和金額行 */}
                    <div className="flex items-center gap-4 mb-2">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getHoursGradientClass(entry.hours)}`}>
                        {entry.hours}h
                      </span>
                      <span className="text-sm text-secondary-700">
                        {formatCurrency(entry.amount)}
                      </span>
                    </div>

                    {/* 工作描述 */}
                    {entry.description && (
                      <div className="pt-2 border-t border-secondary-300">
                        <p className="text-sm text-secondary-700 leading-relaxed">
                          {entry.description}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* 員工申報說明 */}
      {detail.comments && (
        <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-secondary-800 mb-4 flex items-center gap-2">
            <Briefcase className="w-5 h-5 text-primary-600" />
            申報說明
          </h3>
          <div className="bg-secondary-50 rounded-lg p-4">
            <p className="text-secondary-700 whitespace-pre-wrap">{detail.comments}</p>
          </div>
        </div>
      )}

      {/* 審核結果 */}
      {(detail.status === 'APPROVED' || detail.status === 'REJECTED') && (
        <div className="bg-white rounded-xl shadow-soft p-6 border border-primary-200">
          <h3 className="text-lg font-semibold text-secondary-800 mb-4 flex items-center gap-2">
            <Star className="w-5 h-5 text-primary-600" />
            審核結果
          </h3>
          
          <div className="space-y-4">
            {detail.qualityScore && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  品質評分
                </label>
                <div className="flex items-center gap-4">
                  <span className="text-2xl font-bold text-secondary-800">{detail.qualityScore}分</span>
                  <div className="flex items-center gap-1">
                    {renderStars(detail.qualityScore)}
                  </div>
                  <span className={`text-sm font-medium ${getQualityLevel(detail.qualityScore).color}`}>
                    {getQualityLevel(detail.qualityScore).text}
                  </span>
                </div>
              </div>
            )}

            {detail.reviewedAt && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  審核時間
                </label>
                <p className="text-secondary-800">
                  {new Date(detail.reviewedAt).toLocaleString('zh-TW')}
                </p>
              </div>
            )}

            {detail.reviewer && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  審核人員
                </label>
                <p className="text-secondary-800">{detail.reviewer.name}</p>
              </div>
            )}

            {detail.status === 'REJECTED' && detail.rejectionReason && (
              <div>
                <label className="block text-sm font-medium text-secondary-700 mb-2">
                  駁回原因
                </label>
                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                  <p className="text-red-700 whitespace-pre-wrap">{detail.rejectionReason}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}

export default TimesheetDetailPage
